import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Stack,
  Divider,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { auth } from "../../firebase";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

export default function SelectProvider() {
  const navigate = useNavigate();

  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("paid") === "success") {
      setShowSuccess(true);
      navigate("/accountant/select-provider", { replace: true });
    } else if (params.get("paid") === "cancel") {
      alert("⚠️ Payment was not completed.");
      navigate("/accountant/pricing", { replace: true });
    }
  }, [location.search, navigate]);

  const handleSelect = async (provider) => {
    if (provider === "xero" || provider === "brightbooks") {
      alert(
        `${
          provider.charAt(0).toUpperCase() + provider.slice(1)
        } is coming soon!`
      );
      return;
    }

    try {
      await api.post("/accountant/provider", { provider });

      if (provider === "quickbooks") {
        const token = await auth.currentUser.getIdToken();
        window.location.href = `${process.env.REACT_APP_API_BASE_URL}/quickbooks/auth?token=${token}`;
      }
    } catch (err) {
      console.error(err);
      alert("Failed to set provider. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,
        backgroundColor: "#f9fafc",
      }}
    >
      <Box maxWidth="900px" width="100%" textAlign="center">
        <Typography variant="body2" color="text.secondary" mb={1}>
          Step 3 of 3: Connect Your Provider
        </Typography>

        <Typography variant="h4" fontWeight={700} gutterBottom>
          Select Your Accounting Provider
        </Typography>

        <Typography variant="body1" color="text.secondary" mb={4}>
          Connect your accounting software to enable seamless expense and
          invoice syncing. You can always link a provider later from your
          dashboard settings.
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          justifyContent="center"
          alignItems="stretch"
        >
          <Card
            sx={{
              flex: 1,
              borderRadius: 2,
              border: "2px solid #2563eb",
              "&:hover": { boxShadow: 6 },
              transition: "0.2s",
            }}
          >
            <CardActionArea onClick={() => handleSelect("quickbooks")}>
              <CardContent>
                <Typography variant="h6" fontWeight={700}>
                  QuickBooks
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Connect to QuickBooks for expense and invoice syncing.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={1} alignItems="center">
                  <Typography variant="body2">✅ Expense syncing</Typography>
                  <Typography variant="body2">✅ Invoice syncing</Typography>
                  <Typography variant="body2">✅ Most popular</Typography>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card
            sx={{
              flex: 1,
              borderRadius: 2,
              bgcolor: "#f4f6f8",
              opacity: 0.7,
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={700} color="text.secondary">
                Xero (Coming Soon)
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Connect to Xero for expense and invoice syncing.
              </Typography>
            </CardContent>
          </Card>

          <Card
            sx={{
              flex: 1,
              borderRadius: 2,
              bgcolor: "#f4f6f8",
              opacity: 0.7,
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={700} color="text.secondary">
                BrightBooks (Coming Soon)
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          mt={3}
          display="block"
        >
          You can skip this step and connect a provider later from your
          dashboard settings.
        </Typography>

        <Button
          onClick={() => navigate("/accountant")}
          sx={{ mt: 3 }}
          variant="outlined"
        >
          Skip for Now
        </Button>
      </Box>
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          variant="filled"
        >
          Payment successful! You’re now on the Pro plan.
        </Alert>
      </Snackbar>
    </Box>
  );
}
